import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { firstValueFrom, from, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { JwtAuthService } from '../services/auth/jwt-auth.service';
import { Mutex } from 'async-mutex';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private mutex = new Mutex();
  private refreshTokenPromise: Promise<string> | null = null;

  constructor(private jwtAuth: JwtAuthService) {
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const changedReq = this.addTokenHeader(req);

    return next.handle(changedReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (req.url.includes('token')) {
          if (!this.jwtAuth.isLoggedIn) {
            return throwError(() => error);
          }
          this.jwtAuth.logout(true);
          return throwError(() => error);
        }

        if (error && error.status === 401) {
          const refreshToken = this.jwtAuth.getJwtRefreshToken();
          if (!refreshToken) {
            this.jwtAuth.logout(true);
            return throwError(() => error);
          }

          // Evita chamadas múltiplas
          if (!this.refreshTokenPromise) {
            this.refreshTokenPromise = this.mutex.runExclusive(async () => {
              await firstValueFrom(this.jwtAuth.refreshToken());
              return this.jwtAuth.getJwtToken();
            }).finally(() => {
              // limpa após o uso
              this.refreshTokenPromise = null;
            });
          }

          return from(this.refreshTokenPromise).pipe(
            switchMap(newToken => next.handle(this.addTokenHeader(req, newToken))),
            catchError(refreshError => {
              this.jwtAuth.logout(true);
              return throwError(() => refreshError);
            })
          );
        }

        return throwError(() => error);
      })
    );
  }

  private addTokenHeader(req: HttpRequest<any>, token?: string): HttpRequest<any> {
    const tkn = token || this.jwtAuth.token || this.jwtAuth.getJwtToken();
    const excludeUrlS3 = '.s3.us-east-2.amazonaws.com';
    const excludeToken = '/v1/token';
    if (
      tkn &&
      !req.url.includes(excludeUrlS3) &&
      !req.url.includes(excludeToken)
    ) {
      return req.clone({
        setHeaders: {
          Authorization: `Bearer ${ tkn }`,
        },
      });
    }
    return req;
  }
}
